import { SxProps, Theme } from '@mui/material';

/**
 * Les largeurs de colonnes du tableau des billing accounts, partagées entre le
 * Datagrid parent (AccountsBlock) et les lignes projet de son panneau déplié
 * (AccountProjects).
 *
 * ⚠️ Couplage assumé, et c'est tout l'objet de ce fichier. react-admin rend le
 * panneau déplié dans un `<td colSpan>` : les lignes projet vivent donc dans un
 * SECOND `<table>`, qui ne partage aucune colonne avec le parent. Le seul moyen
 * de le faire tomber sous les bons en-têtes est de figer les mêmes largeurs de
 * part et d'autre (`table-layout: fixed`). Deux listes de largeurs, une par
 * fichier, divergeraient en silence : rien ne casse, rien ne type-check en
 * rouge, le sous-tableau se décale simplement d'une demi-colonne.
 *
 * Ajouter ou retirer une colonne au Datagrid impose donc DEUX gestes conjoints :
 * l'entrée ici et la cellule dans `ProjectRow`.
 *
 * Ordre : [dépliage] · Billing account · Abonnement · Projets · Dernier
 * process · Storage · Conso managée · État.
 */
const COLUMN_WIDTHS: readonly (string | null)[] = [
	'48px', // Icône de dépliage, ajoutée par react-admin avant nos champs
	'18rem', // Billing account / nom du projet
	'8.5rem', // Abonnement
	'6.5rem', // Projets
	'8.5rem', // Dernier process
	'7rem', // Storage
	'8rem', // Conso managée
	// État — élastique, absorbe la place restante : ses badges se mettent sur
	// plusieurs lignes dès qu'ils sont à l'étroit, alors qu'un nom de compte tronqué
	// reste lisible.
	null,
];

/**
 * Sous cette largeur, la colonne élastique se retrouve écrasée par les colonnes
 * figées. Le tableau déborde alors de son conteneur et défile horizontalement —
 * le sous-tableau des projets étant imbriqué dans le parent, les deux défilent
 * ensemble et restent alignés. Le groupe orphelin, lui, a son propre conteneur
 * défilant : sur un écran très étroit il peut se désaligner du tableau du dessus.
 */
const TABLE_MIN_WIDTH = 1080;

type CellRules = Record<string, { width: string }>;

/** Une règle de largeur par colonne figée, positionnée par son rang. */
const widthRules = (selector: (position: number) => string): CellRules =>
	COLUMN_WIDTHS.reduce<CellRules>((rules, width, index) => {
		if (width) rules[selector(index + 1)] = { width };
		return rules;
	}, {});

/**
 * Densité commune aux deux tableaux. Le défaut MUI vise des tableaux de trois
 * colonnes : ici il y en a huit, et chaque cellule porte une sous-ligne — le
 * texte au corps par défaut faisait déborder la ligne sur deux hauteurs.
 */
export const densitySx = {
	'& td, & th': { paddingTop: 0.5, paddingBottom: 0.5, fontSize: '0.875rem' },
	// Les en-têtes restent lisibles sans crier : c'est la casse et non la taille
	// qui les distingue des cellules.
	'& th': { fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.02em' },
	'& .MuiChip-root': { height: 22, fontSize: '0.75rem' },
};

/** À passer en `sx` au Datagrid parent : il pose la grille de référence. */
export const datagridColumnsSx: SxProps<Theme> = {
	'& .RaDatagrid-tableWrapper': { overflowX: 'auto' },
	'& .RaDatagrid-table': { tableLayout: 'fixed', minWidth: TABLE_MIN_WIDTH },
	...densitySx,
	...widthRules(
		(position) =>
			`& .RaDatagrid-headerRow > th:nth-of-type(${position}), & .RaDatagrid-row > td:nth-of-type(${position})`
	),
	// Le panneau déplié est un `<td>` comme un autre : son padding par défaut
	// (16px) pousserait tout le sous-tableau vers la droite, et l'alignement
	// colonne par colonne serait faux d'entrée.
	'& .RaDatagrid-expandedPanel > td': { padding: 0 },
};

/**
 * À passer en `sx` aux tableaux secondaires (projets dépliés, groupe orphelin) :
 * mêmes colonnes, et surtout aucun `<TableHead>`. Un second bandeau d'en-têtes
 * au milieu du tableau se lit comme un nouveau tableau — point dur déjà corrigé
 * une fois, ne pas le réintroduire.
 */
export const secondaryTableSx: SxProps<Theme> = {
	tableLayout: 'fixed',
	minWidth: TABLE_MIN_WIDTH,
	...densitySx,
	...widthRules((position) => `& td:nth-of-type(${position})`),
};
